import { Injectable, NgZone } from '@angular/core'
import {
    BehaviorSubject,
    Observable,
    ReplaySubject,
    Subscription,
    catchError,
    filter,
    map,
    mergeWith,
    of,
    skip,
    switchMap,
    tap,
    throttleTime,
} from 'rxjs'
import { NumberPublisher } from '@app/domain/NumberPublisher'
import { ContextService } from '@services/context.service'
import { SseService } from '@services/sse.service'
import { WaitlistService } from '@services/waitlist.service'
import { DateTime } from 'luxon'

@Injectable({
    providedIn: 'root',
})
export class WaitlistEntriesCountPublisher implements NumberPublisher {

    private number = new BehaviorSubject<number>(0)
    public number$ = this.number.asObservable()
    private loading = new BehaviorSubject<boolean>(true)
    public loading$ = this.loading.asObservable()
    private refreshRateSeconds = 60 * 15
    private pollingSubscription: Subscription | null = null
    private refreshTrigger$ = new ReplaySubject<void>(1)
    private trackedDate: Date | null = null

    constructor(
        private waitlistService: WaitlistService,
        private contextService: ContextService,
        private sseService: SseService,
        private zone: NgZone,
    ) { }

    startPolling() {
        if (this.pollingSubscription) {
            return
        }
        this.pollingSubscription = this.zone.runOutsideAngular(() => this.makePolling().subscribe())
        this.refreshNow()
    }

    setTrackedDate(date: Date | null) {
        this.trackedDate = date
        if (date === null) {
            this.loading.next(false)
        }
        this.refreshNow()
    }

    refreshNow() {
        this.refreshTrigger$.next()
    }

    private makePolling() {
        const sseWaitlistEvents$ = this.sseService.events$.pipe(
            filter(event => event.eventType === 'waitlist_entry_created' || event.eventType === 'waitlist_entry_deleted'),
            filter(event => this.isSseEventRelevantToTrackedDate(event.waitlistEntryDate)),
            map(() => false),
        )
        const venueChanges$ = this.contextService.venue$.pipe(
            skip(1),
            map(() => true),
        )
        const periodicRefresh$ = this.emitPeriodically().pipe(map(() => false))
        const manualRefresh$ = this.refreshTrigger$.pipe(map(() => false))
        return sseWaitlistEvents$
            .pipe(
                mergeWith(venueChanges$),
                mergeWith(periodicRefresh$),
                mergeWith(manualRefresh$),
                throttleTime(1000, undefined, { leading: true, trailing: true }),
                tap(shouldShowLoading => {
                    if (shouldShowLoading) {
                        this.zone.run(() => this.loading.next(true))
                    }
                }),
                switchMap(() => this.getWaitlistEntryCountForTrackedDate()),
                catchError(() => of(0)),
                tap(count => {
                    this.zone.run(() => {
                        this.number.next(count)
                        this.loading.next(false)
                    })
                })
            )
    }

    private emitPeriodically() {
        return new Observable<void>((observer) => {
            let timeoutId: ReturnType<typeof setTimeout> | null = null
            const emitFunction = () => {
                observer.next()
                timeoutId = setTimeout(emitFunction, this.refreshRateSeconds * 1000)
            }
            emitFunction()
            return () => {
                if (timeoutId !== null) {
                    clearTimeout(timeoutId)
                }
            }
        })
    }

    private getWaitlistEntryCountForTrackedDate() {
        const business = this.contextService.getBusiness()
        const venue = this.contextService.getVenue()
        if (!business || !venue || !venue.waitlistsEnabled || !this.trackedDate) {
            return of(0)
        }
        return this.waitlistService.getWaitlistEntryCount(business.id, venue.id, this.trackedDate)
    }

    private isSseEventRelevantToTrackedDate(waitlistEntryDate?: string): boolean {
        const venue = this.contextService.getVenue()
        if (!waitlistEntryDate || !this.trackedDate || !venue) {
            return false
        }
        const eventDate = DateTime.fromISO(waitlistEntryDate, { zone: venue.timeZone }).startOf('day')
        const trackedDate = DateTime.fromJSDate(this.trackedDate).setZone(venue.timeZone).startOf('day')
        return eventDate.equals(trackedDate)
    }
}